import type { AppNotification } from '@digital-twins/shared-types'
import { ROUTES } from '@/routes/routes'

/** Flip to false to disable mock notifications in local dev. */
export const DEV_MOCK_NOTIFICATIONS = import.meta.env.DEV

/** Platform admins in dev get mock announcements to preview the recipient experience. */
export function shouldUseDevMockNotifications(
  isPlatformAdmin: boolean | undefined,
  email?: string | null,
): boolean {
  if (!DEV_MOCK_NOTIFICATIONS) {
    return false
  }

  if (isPlatformAdmin === true) {
    return true
  }

  const normalised = email?.trim().toLowerCase()
  return !!normalised && normalised.endsWith('@fairgen.ai')
}

export function getDevMockNotifications(): Array<AppNotification> {
  const now = Date.now()
  const hour = 3_600_000

  return [
    {
      id: 'dev-mock-announcement-photo',
      type: 'cs_announcement',
      message: 'Introducing smarter audience insights',
      href: '/',
      createdAt: now - 60_000,
      metadata: {
        body: 'Explore new ways to compare audiences, run faster studies, and share results with your team.',
        imageUrl: 'https://picsum.photos/seed/fairgen-announcement/640/320',
      },
    },
    {
      id: 'dev-mock-notification-1',
      type: 'study_completed',
      message: 'Your study "Q1 Brand Tracker" completed',
      href: '/tests',
      metadata: { studyId: 'mock-study-1', studyName: 'Q1 Brand Tracker' },
      createdAt: now - hour * 0.25,
    },
    {
      id: 'dev-mock-notification-2',
      type: 'audience_ready',
      message: 'Audience "Revenue Growth" is ready to use',
      href: '/audiences',
      metadata: { audienceId: '10', audienceName: 'Revenue Growth' },
      createdAt: now - hour * 1,
    },
    {
      id: 'dev-mock-notification-3',
      type: 'chat_shared',
      message: 'Alex shared a conversation with you',
      href: '/chats',
      metadata: {
        chatId: 'mock-chat-1',
        chatName: 'Brand positioning brainstorm',
        sharerName: 'Alex',
      },
      createdAt: now - hour * 2,
    },
    {
      id: 'dev-mock-notification-4',
      type: 'credit_topup',
      message: '500 credits were added to your workspace',
      href: ROUTES.settingsTab('billing'),
      metadata: { creditAmount: 500 },
      createdAt: now - hour * 5,
    },
    {
      id: 'dev-mock-notification-5',
      type: 'low_credit',
      message: 'You have fewer than 100 credits remaining',
      href: ROUTES.settingsTab('billing'),
      metadata: { creditBalance: 100 },
      createdAt: now - hour * 8,
    },
    {
      id: 'dev-mock-notification-6',
      type: 'team_member_joined',
      message: 'Jamie joined your workspace',
      href: ROUTES.settingsTab('account'),
      metadata: { memberName: 'Jamie' },
      createdAt: now - hour * 12,
    },
    {
      id: 'dev-mock-notification-7',
      type: 'study_failed',
      message: 'Study "Concept Test A" failed during simulation',
      href: '/tests',
      metadata: { studyId: 'mock-study-2', studyName: 'Concept Test A' },
      createdAt: now - hour * 18,
    },
    {
      id: 'dev-mock-notification-8',
      type: 'referral_credit',
      message: 'You earned 300 bonus credits from a referral',
      href: ROUTES.settingsTab('billing'),
      metadata: { creditAmount: 300 },
      createdAt: now - hour * 24,
    },
    {
      id: 'dev-mock-notification-9',
      type: 'trial_expiring',
      message: 'Your trial ends in 3 days',
      href: ROUTES.billingPlans,
      metadata: { daysRemaining: 3 },
      createdAt: now - hour * 30,
    },
    {
      id: 'dev-mock-notification-10',
      type: 'cs_announcement',
      message: 'New: Focus groups now support up to 8 participants',
      createdAt: now - hour * 36,
    },
  ]
}

/** In dev mocks, only the first three list items appear unread. */
export function getDevMockLastReadAt(mockItems: Array<AppNotification>): number {
  if (mockItems.length <= 3) {
    return 0
  }

  return mockItems[3].createdAt + 1
}
